import { node } from 'prop-types'
import { useMemo, useState } from 'react'
import { Divider, Header, List, Tab } from 'semantic-ui-react'

import Spectrogram from './components/Spectrogram'
import BurstWarningBanner from '../SolarFluxLive/components/BurstWarningBanner'
import DataStatusIndicator, { DataStatus } from '../SolarFluxLive/components/DataStatusIndicator'
import { useBurstStatus } from '../SolarFluxLive/hooks/useBurstStatus'
import { useTechnicalDay } from '../SolarFluxLive/hooks/useTechnicalDay'

function RadioSpectrogram() {
  const [activeTab, setActiveTab] = useState(0)
  const burstStatus = useBurstStatus(activeTab, 'RadioSpectrogram')
  const [hasDataF107, setHasDataF107] = useState(false)
  const [hasDataF30, setHasDataF30] = useState(false)
  const isTechnicalDay = useTechnicalDay()

  // Compute burst data status based on current tab's data availability
  const burstDataStatus: DataStatus = useMemo(() => {
    if (isTechnicalDay) {
      return 'technical-day'
    }
    const hasData = activeTab === 0 ? hasDataF107 : hasDataF30
    return hasData ? 'data-flowing' : 'no-observation'
  }, [activeTab, hasDataF107, hasDataF30, isTechnicalDay])

  const panes = [
    {
      menuItem: 'F10.7',
      render: () => (
        <div>
          <Spectrogram type='10' onHasDataChange={setHasDataF107} />
          <Divider />

          <div style={{ textAlign: 'justify' }}>
            <Header as='h3' style={{ marginTop: '0.5em' }}>
              Description
            </Header>

            <div style={{ marginBottom: '1em' }}>
              The spectrograms display solar radio bursts in real time. When such events occur, they are plotted live in
              the F10.7&nbsp;(≈&nbsp;2.8&nbsp;GHz) and F30&nbsp;(≈&nbsp;1&nbsp;GHz) frequency bands.
            </div>

            <List bulleted>
              <List.Item>
                The spectrogram provides high frequency resolution, consisting of 1024 subchannels - each sample
                corresponds to approximately 100&nbsp;kHz.
              </List.Item>

              <List.Item>
                The x-axis represents time, with a one-second sampling interval for each subchannel.
              </List.Item>

              <List.Item>
                The color scale (heatmap) indicates flux density, expressed in solar flux units (
                SFU), where 1&nbsp;SFU&nbsp;=&nbsp;10<sup>-22</sup>&nbsp;Wm<sup>-2</sup>Hz<sup>-1</sup>
                &nbsp;(SI units).
              </List.Item>
            </List>
          </div>
        </div>
      ),
    },
    {
      menuItem: 'F30',
      render: () => (
        <div>
          <Spectrogram type='30' onHasDataChange={setHasDataF30} />
          <Divider />

          <div style={{ textAlign: 'justify' }}>
            <Header as='h3' style={{ marginTop: '0.5em' }}>
              Description
            </Header>

            <div style={{ marginBottom: '1em' }}>
              The spectrograms display solar radio bursts in real time. When such events occur, they are plotted live in
              the F10.7&nbsp;(≈&nbsp;2.8&nbsp;GHz) and F30&nbsp;(≈&nbsp;1&nbsp;GHz) frequency bands.
            </div>

            <List bulleted>
              <List.Item>
                The spectrogram provides high frequency resolution, consisting of 1024 subchannels - each sample
                corresponds to approximately 100&nbsp;kHz.
              </List.Item>

              <List.Item>
                The x-axis represents time, with a one-second sampling interval for each subchannel.
              </List.Item>

              <List.Item>
                The color scale (heatmap) indicates flux density, expressed in solar flux units (SFU),
                where 1&nbsp;SFU&nbsp;=&nbsp;10<sup>-22</sup>&nbsp;Wm<sup>-2</sup>Hz<sup>-1</sup>&nbsp;(SI units).
              </List.Item>
            </List>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className='fade-in'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Header as='h1' style={{ marginBottom: 0 }}>
          Radio Spectrograms
          <Header.Subheader>A visual representation of solar radio activity across frequency bands</Header.Subheader>
        </Header>
        <DataStatusIndicator label='Burst' status={burstDataStatus} />
      </div>

      <BurstWarningBanner status={burstStatus} />

      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        className='secondary_tab'
        activeIndex={activeTab}
        onTabChange={(_, { activeIndex }) => setActiveTab((activeIndex as number) ?? 0)}
      />
    </div>
  )
}

RadioSpectrogram.propTypes = {
  children: node,
}

export default RadioSpectrogram
